import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { useMemo, useEffect, useState } from "react";
import { useExpectedMonthlyIncome } from "@src/api/useExpectedMonthlyIncome";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@src/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@src/components/ui/radio-group";
import { Skeleton } from "@src/components/ui/skeleton";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";
import { useKYCAddUserLog, LoggerTypes } from "@src/api/useKYCAddUserLog";
import { AlertTriangle } from "lucide-react";

const FormSchema = z.object({
  monthlyIncomeCode: z.string().refine((v) => v),
});

export const SAKARTVELO_COUNTRY_CODE = 79;

function ExpectedMonthlyIncome() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: zData, setData } = useZustandStore((state) => state);
  const isResident = useMemo(
    () => zData.closeSession.countryID === SAKARTVELO_COUNTRY_CODE,
    [zData.closeSession.countryID]
  );
  const expectedMonthlyIncomes = useExpectedMonthlyIncome(isResident);
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      monthlyIncomeCode: zData.monthlyIncomeCode,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.ExpectedMonthlyIncomes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      monthlyIncomeCode: data.monthlyIncomeCode,
    });
    navigate(`/${i18n.language}/annual-transactions`);
  }

  const selectedIncomeCode = form.watch("monthlyIncomeCode");

  useEffect(() => {
    if (
      selectedIncomeCode === "1000000>=-NonResident" ||
      selectedIncomeCode === "1000000>=-Resident"
    ) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [selectedIncomeCode]);

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc
            desc={t("expectedMonthlyIncomeCode.desc")}
            title={`${t("expectedMonthlyIncomeCode.title")} (${
              isResident ? "GEL" : "USD"
            })`}
          />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* name */}
          <FormField
            control={form.control}
            name="monthlyIncomeCode"
            render={({ field }) => {
              return (
                <FormItem>
                  <RadioGroup
                    className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    {expectedMonthlyIncomes.isLoading
                      ? [1, 2, 3, 4].map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between gap-1 border-b border-solid border-gray-200",
                                "last:border-b-0 relative"
                              )}
                              key={el}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 h-14 w-full">
                                <Skeleton className="h-5 w-[100px]" />
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <Skeleton className="h-5 w-5 rounded-full" />
                              </FormControl>
                            </FormItem>
                          );
                        })
                      : expectedMonthlyIncomes.data?.map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between gap-1 border-b relative border-solid border-gray-200",
                                "last:border-b-0"
                              )}
                              key={el.expectedMonthlyIncomeCode}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 h-14 w-full cursor-pointer">
                                {el.expectedMonthlyIncome}
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <RadioGroupItem
                                  value={el.expectedMonthlyIncomeCode}
                                />
                              </FormControl>
                            </FormItem>
                          );
                        })}
                  </RadioGroup>
                </FormItem>
              );
            }}
          />
          {open && (
            <div className="flex bg-orange-50 border border-solid border-orange-200 rounded-lg p-4 items-center gap-4">
              <AlertTriangle color="#ea580c" size={100} />
              <span className="text-sm font-normal leading-6 text-orange-600">
                {t("expectedMonthlyIncomeCode.infoText")}
              </span>
            </div>
          )}
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default ExpectedMonthlyIncome;
